import { useState } from 'react';
import { SiMicrosoftexcel, SiFiles } from "react-icons/si";
import ModuleButton from '@components/button/ModuleButton'
import useDownloadReport from '../hooks/useDownloadReport';
import AlertModal from '@components/modal/AlertModal';
import TranslatableText from '@components/translations/TranslatableText';
import { useAppDispatch, useAppNavigate, useAppSelector, usePermissions } from '@app/hooks';
import { selectCurrentProject } from '@features/project/projectSlice';
import { setModuleList } from '@features/builder/builderSlice';
import { encodeActivityId } from '@utils/projectIdEncoder';
import NarrativeReportModal from './NarrativeReportModal';

const DownloadReportButtons = ({ selectedActivities, projectId, projectName }: { selectedActivities?: number[], projectId: number, projectName?: string }) => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { isPublic } = usePermissions();
  const { activity: currentActivity } = useAppSelector(selectCurrentProject);
  const {
    downloadExcel,
    downloadNarrativeReport,
    isLoading,
    handleCancel,
    errorMsg,
    isOpen,
    emailNoticeOpen,
    closeEmailNotice,
    readinessOpen,
    notReadyActivities,
    closeReadinessModal,
  } = useDownloadReport({ projectId, projectName, activities: selectedActivities });
  const visibleNotReadyActivities = notReadyActivities.slice(0, 3);
  const remainingActivityCount = notReadyActivities.length - visibleNotReadyActivities.length;
  const [narrativeModalOpen, setNarrativeModalOpen] = useState(false);

  const navigateToActivity = (activityId: number) => {
    let activityIdToUse: number | string = activityId;
    if (isPublic) {
      try {
        activityIdToUse = encodeActivityId(activityId);
      } catch (error) {
        console.error('Failed to encode activity ID:', error);
      }
    }

    if (activityId !== currentActivity?.id) {
      dispatch(setModuleList([]));
    }

    closeReadinessModal();
    navigate({ path: `/builder/${activityIdToUse}` });
  };

  return (
    <div className='d-flex f-column gap-8 pb-2 me-1'>
      <ModuleButton
        icon={SiFiles}
        buttonType="button"
        handleClick={() => setNarrativeModalOpen(true)}
        labelKey="main.download_narrative_report"
        isLoading={isLoading}
        disabled={!projectId}
        className="no-wrap"
      />

      <ModuleButton
        icon={SiMicrosoftexcel}
        buttonType="button"
        handleClick={downloadExcel}
        labelKey="main.download_excel"
        isLoading={isLoading}
        disabled={!projectId}
        className="no-wrap"
      />

      <NarrativeReportModal
        open={narrativeModalOpen}
        onClose={() => setNarrativeModalOpen(false)}
        onDownload={downloadNarrativeReport}
      />

      <AlertModal
        modalOpen={isOpen}
        handleSubmit={handleCancel}
        handleCancel={handleCancel}
      >
        <div className='p-2'>
          {
            errorMsg ? errorMsg : <TranslatableText translationKey="project.report_error" />
          }
        </div>
      </AlertModal>

      <AlertModal
        modalOpen={emailNoticeOpen}
        handleCancel={closeEmailNotice}
        headerText="project.report_via_email"
      >
        <div className='p-2 fs-14'>
          <TranslatableText translationKey="project.report_via_email_message" />
        </div>
      </AlertModal>

      <AlertModal
        modalOpen={readinessOpen}
        handleCancel={closeReadinessModal}
        headerText={notReadyActivities.length === 1
          ? "project.activity_incomplete"
          : "project.activities_incomplete"}
      >
        <div className='p-2 d-flex f-column gap-8 fs-14'>
          <TranslatableText translationKey="project.report_requires_ready_activities" />
          <ul className='m-0 ps-3'>
            {visibleNotReadyActivities.map(activity => (
              <li key={activity.id} className='pb-1'>
                <button
                  type="button"
                  className="clickable bg-transparent underline text-quat-de p-0 fs-14"
                  style={{ border: 0, textAlign: 'left' }}
                  onClick={() => navigateToActivity(activity.id)}
                >
                  {activity.name}
                </button>
              </li>
            ))}
          </ul>
          {remainingActivityCount > 0 && (
            <TranslatableText
              translationKey="project.report_more_activities"
              translationParams={{ count: remainingActivityCount.toString() }}
            />
          )}
        </div>
      </AlertModal>
    </div>

  )
}

export default DownloadReportButtons